/**
 * The scalar coercions every mapper needs, in ONE place.
 *
 * Each mapper used to carry its own copy of `str` / `strOrNull` / `num`, which drifted:
 * `rfq.ts` had no `strOrNull` (so `Rfq.sent_at`, typed `string | null`, got `''`), and the
 * local `num` returned 0 for the string `"240"` while `money()` in the same document happily
 * coerced it. One document, two rules. These are the rules now — `num` delegates to the very
 * function `money()` uses, so a numeric string means the same thing everywhere.
 *
 * Mappers must never throw: Frappe is loosely typed on the wire and a single malformed row
 * must degrade to a default, not take a screen down.
 */

import { toAmount } from '../../money'

export function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

/** `null` when absent/blank rather than coercing to `''`, for fields the type marks nullable. */
export function strOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/**
 * Numbers, and numeric strings, coerced exactly as `money()` coerces an amount — Frappe
 * returns Decimal-backed fields as strings often enough that the two must agree.
 * Anything that is neither a number nor a string (null, object, undefined) takes `fallback`.
 */
export function num(value: unknown, fallback = 0): number {
  if (typeof value !== 'number' && typeof value !== 'string') return fallback
  return toAmount(value)
}

/**
 * A Frappe `Check` field, coerced to a real boolean.
 *
 * Frappe stores a Check as an integer 0/1, but what arrives on the wire depends on the path: the
 * REST layer sends `1`, a form submission or a hand-written filter can send the STRING `"1"`, and
 * some client code sends a real `true`. All three mean checked.
 *
 * This lives here because the two Check readers in `opportunity.ts` had drifted apart: one
 * accepted `'1'` and the other did not, so an `Opportunity Item` whose `create_rfq` arrived as the
 * string `"1"` read as a contract-routed line and silently dropped out of RFQ generation. One rule
 * now, used by both.
 *
 * Anything else — `0`, `'0'`, `''`, `null`, absent, an object — is false.
 */
export function bool(value: unknown): boolean {
  return value === 1 || value === true || value === '1'
}

/**
 * A child table's rows, with anything that is not a plain object dropped.
 *
 * `doc.items.map((row) => f(row as Record<string, unknown>))` was the only crash path left in
 * otherwise non-throwing mappers: a `null` element throws on the first property read. Frappe
 * does not normally send one, but the mappers' whole contract is that a weird document
 * produces a degraded object rather than an exception, so the possibility is filtered out
 * rather than asserted away.
 */
export function childRows(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (row): row is Record<string, unknown> => typeof row === 'object' && row !== null && !Array.isArray(row),
  )
}
